import { useEffect } from "react";
import {
  Outlet,
  Navigate,
  createRootRouteWithContext,
  useLocation,
  useRouter,
} from "@tanstack/react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import { queryClient } from "@/lib/queryClient";
import { watchSession } from "@/lib/sessionGuards";
import { AuthProvider } from "@lato/client/contexts/AuthContext";
import { PlatformProvider, type PlatformConfig } from "@lato/client/contexts/PlatformContext";
import { TooltipProvider } from "@lato/client/components/ui/tooltip";
import { ErrorBoundary } from "@lato/client/components/ErrorBoundary";
import { Button } from "@lato/client/components/ui/button";
import { LoadErrorState } from "@/components/LoadErrorState";
import { setClientTraceRoute } from "@lato/client/lib/clientTraceAuth";
import { initializeLogfire } from "@lato/client/lib/logfire";
import { PostHogProvider } from "posthog-js/react";
import { POSTHOG_KEY, POSTHOG_OPTIONS } from "@/lib/posthog";
import { AnalyticsBridge } from "@/components/AnalyticsBridge";
import { ConsentManager } from "@/components/ConsentManager";
import type { RouterContext } from "@/router";

const webPlatform: PlatformConfig = {
  shortcuts: { focusChat: "", newAgent: "" },
  useSelection: () => {},
  navigateToRange: () => {},
};

function RootComponent() {
  const location = useLocation();

  useEffect(() => {
    initializeLogfire("web");
  }, []);

  // This shell survives a child render crash, so only it can distinguish an error-boundary
  // unmount from a genuine route change when retiring an anonymous interview trace proof.
  useEffect(() => {
    setClientTraceRoute(location.pathname);
  }, [location.pathname]);

  const router = useRouter();
  useEffect(() => watchSession(router, queryClient), [router]);

  const app = (
    <ErrorBoundary name="RootApp" fallback={<AppCrashed />}>
      <PlatformProvider value={webPlatform}>
        <QueryClientProvider client={queryClient}>
          {/* One shared provider so tooltips get hover-intent + instant-follow (see tooltip.tsx). */}
          <TooltipProvider>
            <AuthProvider>
              <AnalyticsBridge />
              <ConsentManager />
              <Outlet />
            </AuthProvider>
          </TooltipProvider>
          <Toaster
            position="top-right"
            style={{ right: 20 }}
            icons={{ error: null, success: null, warning: null, info: null }}
            closeButton
            toastOptions={{
              unstyled: true,
              duration: 4000,
              classNames: {
                toast:
                  "px-2.5 py-1.5 pr-6 rounded text-caption shadow-sm border bg-background/95 backdrop-blur-sm text-foreground relative transition-opacity duration-200",
                error: "border-destructive/30 text-destructive",
                success: "border-emerald-500/30 text-emerald-600",
                warning: "border-amber-500/30 text-amber-600",
                info: "border-blue-500/30 text-blue-600",
                closeButton:
                  "absolute right-1 top-1/2 -translate-y-1/2 size-4 flex items-center justify-center rounded opacity-40 hover:opacity-100 hover:bg-black/5 border-0 bg-transparent",
              },
            }}
          />
        </QueryClientProvider>
      </PlatformProvider>
    </ErrorBoundary>
  );

  // Mount the PostHog provider only when configured (per PostHog's React guidance: reach the
  // client via `usePostHog()`, never a direct import). No key → no provider → analytics inert.
  return POSTHOG_KEY ? (
    <PostHogProvider apiKey={POSTHOG_KEY} options={POSTHOG_OPTIONS}>
      {app}
    </PostHogProvider>
  ) : (
    app
  );
}

/**
 * Last-resort fallback. Renders outside every provider and the router, so no hooks or `Link`:
 * a plain reload is the only recovery. Route-level failures never reach this any more (see
 * `defaultErrorComponent` in `router.ts`); this is for errors in the shell itself.
 */
function AppCrashed() {
  return (
    <LoadErrorState
      title="Something went wrong"
      description="The error has been logged. Reload the page to continue."
    >
      <Button variant="outline" onClick={() => window.location.reload()}>
        Reload page
      </Button>
    </LoadErrorState>
  );
}

function PendingComponent() {
  return (
    <div className="flex h-full items-center justify-center text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin" />
    </div>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  pendingComponent: PendingComponent,
  notFoundComponent: () => <Navigate to="/" />,
});
